import { Action, Selector, State, StateContext } from "@ngxs/store";
import { NotificationsService } from "../../services/notifications/notification.service";
import { Injectable } from "@angular/core";
import { NotificationActions } from "./notification.action";
import { catchError, tap, throwError, takeUntil } from "rxjs";
import { INotification, INotificationSseEvent } from "../../interfaces/notifications.interface";
import { NotificationType } from "../../enums/notifications";

export class NotificationStateModel {
    notifications: INotification[] = [];
    notification: INotification | null = null;
    loading: boolean = false;
    error: any | null = null;
    isPollingActive: boolean = false;
  }
  
  @State<NotificationStateModel>({
    name: 'notification',
    defaults: {
      notifications: [],
      notification: null,      
      loading: false,
      error: null,
      isPollingActive: false,
    },
  })
  
  @Injectable()
  export class NotificationState {
    constructor(private notificationService: NotificationsService) {

    }
  
    @Selector()
    static getNotifications(state: NotificationStateModel): INotification[] {
      return state.notifications;
    }
  
    @Selector()
    static getNotification(state: NotificationStateModel): INotification | null {
      return state.notification;
    }

    @Selector()
    static getNewNotifications(state: NotificationStateModel): INotification[]| null {
      return state.notifications.filter(notif=> !notif.readed);
    }

     @Selector()
    static getReadedNotifications(state: NotificationStateModel): INotification[]| null {
      return state.notifications.filter(notif=> notif.readed);
    }
  
    @Selector()
    static getLoading(state: NotificationStateModel): boolean {
      return state.loading;
    }
  
    @Selector()
    static getError(state: NotificationStateModel): any | null {
      return state.error;
    }

    @Selector()
    static isPollingActive(state: NotificationStateModel): boolean {
      return state.isPollingActive;
    }

    @Selector()
    static getNewNotificationsCount(state: NotificationStateModel): number {
      return state.notifications.filter(notif=> !notif.readed).length;
    }


  
  
 
    @Action(NotificationActions.GetNotifications)
    getNotifications(ctx: StateContext<NotificationStateModel>) {
      ctx.patchState({ loading: true, error: null });
      return this.notificationService.getNotifications().pipe(
        tap((response) => {
          console.log('response getNotifications', response);
          ctx.patchState({ notifications: this.deduplicate(response.data), loading: false });
        }),
        catchError((error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
          return throwError(() => error);
        })
      );
    }

    @Action(NotificationActions.StartPolling)
    startPolling(ctx: StateContext<NotificationStateModel>, action: NotificationActions.StartPolling) {
      ctx.patchState({ isPollingActive: true });
      return this.notificationService.startNotificationPolling(action.intervalMs).pipe(
        tap((response) => {
          console.log('Polling response:', response);
          ctx.patchState({ notifications: this.deduplicate(response.data) });
        }),
        catchError((error) => {
          console.error('Polling error:', error);
          ctx.patchState({ isPollingActive: false, error: error.error?.message });
          return throwError(() => error);
        })
      );
    }

    @Action(NotificationActions.StopPolling)
    stopPolling(ctx: StateContext<NotificationStateModel>) {
      this.notificationService.stopNotificationPolling();
      ctx.patchState({ isPollingActive: false });
    }

    @Action(NotificationActions.RefreshNotifications)
    refreshNotifications(ctx: StateContext<NotificationStateModel>) {
      ctx.patchState({ loading: true, error: null });
      return this.notificationService.refreshNotifications().pipe(
        tap((response) => {
          console.log('Refresh response:', response);
          ctx.patchState({ notifications: this.deduplicate(response.data), loading: false });
        }),
        catchError((error) => {
          ctx.patchState({ loading: false, error: error.error?.message });
          return throwError(() => error);
        })
      );
    }

    private deduplicate(notifications: INotification[]): INotification[] {
      const seen = new Map<number, INotification>();
      for (const n of notifications) {
        if (!seen.has(n.id) || n.readed) {
          seen.set(n.id, n);
        }
      }
      return Array.from(seen.values());
    }

    @Action(NotificationActions.MarkAsRead)
    markAsRead(ctx: StateContext<NotificationStateModel>, action: NotificationActions.MarkAsRead) {
      return this.notificationService.markAsRead(action.notificationIds).pipe(
        tap(() => {
          console.log('action.notificationIds', action.notificationIds);
          ctx.patchState({ notifications: ctx.getState().notifications.map(notif => action.notificationIds.includes(notif.id) ? { ...notif, readed: true } : notif) });
        })
      );
    }

    @Action(NotificationActions.MarkAllAsRead)
    markAllAsRead(ctx: StateContext<NotificationStateModel>) {
      return this.notificationService.markAllAsRead().pipe(
        tap(() => {
          console.log('All notifications marked as read');
          ctx.patchState({ notifications: ctx.getState().notifications.map(notif => ({ ...notif, readed: true })) });
        })
      );
    }

    @Action(NotificationActions.AddFromSse)
    addFromSse(ctx: StateContext<NotificationStateModel>, action: NotificationActions.AddFromSse) {
      const event = action.event;
      const newItem: INotification = {
        id: parseInt(event.id, 10) || Date.now(),
        from: 'system',
        subject: event.title,
        to: [],
        message: event.description,
        type: NotificationType.PUSH,
        origin: event.origin,
        readed: false,
        deleted: false,
        entityType: event.entityType,
        entityId: event.entityId,
        trackingNumber: event.trackingNumber ?? undefined,
        eventType: event.eventType,
        newStatus: event.newStatus,
        previousStatus: event.previousStatus,
        newExternalStatus: event.newExternalStatus,
        previousExternalStatus: event.previousExternalStatus,
        newPriority: event.newPriority,
        previousPriority: event.previousPriority,
        motivo: event.motivo,
        actorName: event.actorName,
        createdAt: new Date(event.createdAt),
        updatedAt: new Date(event.createdAt),
      };
      const notifications = [newItem, ...ctx.getState().notifications];
      ctx.patchState({ notifications });
    }


  }